import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  ImportUserPlanRequestRow,
  ImportUserPlanRow,
  JobOrder,
  JobOrderStatusEnum,
  TagConsolidationRequestDto,
  TagConsolidationResultDto,
} from '@models';
import { AccountUnitOfWork, DbSaveService, EntityFns } from '@data';
import { MessageBusService, MessageTypeEnum, UtilDialogService } from '@core';
import { AccountDbQueryService } from './account-db-query.service';
import * as _ from 'lodash';

export interface SupplierPostResult {
  SupplierId: string;
  StatusCode: number;
  ErrorMessage?: string;
  ConfirmationKey?: string;
}

/**
 * Mirrors the server's SendInviteResult wrapper. `status` is the string form of the
 * server's SendInviteOutcome enum: "Sent" | "AlreadyConfirmed" | "NoEmail" |
 * "UserNotFound" | "AccessDisabled". Callers should react to this — not to a raw
 * true/false — since the server suppresses the email when the user is already
 * confirmed but the HTTP call still succeeds.
 */
export type SendInviteStatus = 'Sent' | 'AlreadyConfirmed' | 'NoEmail' | 'UserNotFound' | 'AccessDisabled';
export interface SendInviteResult {
  status: SendInviteStatus;
}

export interface NotificationRequest {
  // either NotificationEventId or NotificationTemplateId must be filled in.
  NotificationEventId?: number;
  NotificationTemplateId?: string;
  FreeFormTemplate?: string;
  QueryMap: object; // just a standard object with keys and values as strings.
  AccountId: string;
  ProximityUserId: string;
  Priority: number;
  ExpirationDays?: number;
}

@Injectable({ providedIn: 'root' })
export class AccountDbSaveService extends DbSaveService {
  constructor(
    public override uow: AccountUnitOfWork,
    public dbQueryService: AccountDbQueryService,
    public messageBusService: MessageBusService,
    http: HttpClient,
    public dialogService: UtilDialogService,
  ) {
    super(uow, http);
  }

  async deleteJobOrder(jobOrder: JobOrder, shouldSave = true) {
    if (jobOrder.isPlacedOrClosed()) {
      return false;
    }
    const [jo, joNotes, joAddOns, joPayments] = await Promise.all([
      this.dbQueryService.getJobOrderById(jobOrder.id),
      this.dbQueryService.getJobOrderNotes(jobOrder.id),
      this.dbQueryService.getJobOrderDetailAddOnsByJobOrderId(jobOrder.id),
      this.dbQueryService.getJobOrderOveragePayments(jobOrder.id),
    ]);

    joNotes.slice().forEach((p) => EntityFns.deleteOrDetach(p.entityAspect));
    joAddOns.slice().forEach((p) => EntityFns.deleteOrDetach(p.entityAspect));
    joPayments.slice().forEach((p) => EntityFns.deleteOrDetach(p.entityAspect));
    jo.jobOrderDetails.slice().forEach((p) => p.jobOrderDetailAddons.forEach((q) => EntityFns.deleteOrDetach(q.entityAspect)));
    jobOrder.jobOrderDetails.slice().forEach((p) => EntityFns.deleteOrDetach(p.entityAspect));

    EntityFns.deleteOrDetach(jo.entityAspect);
    if (shouldSave) {
      const res = await this.saveChanges();
      return res.entities?.length > 0;
    } else {
      return true;
    }
  }

  async postJobOrder(jobOrderId: string) {
    const r = await this.postData<SupplierPostResult[]>('PostJobOrder', { guid: jobOrderId }, null);
    return r;
  }

  async postReturnRequest(returnRequestId: string) {
    const r = await this.postData<SupplierPostResult[]>('PostReturnRequest', { guid: returnRequestId }, null);
    return r;
  }

  async postCancellationRequest(cancellationRequestId: string) {
    const r = await this.postData<SupplierPostResult[]>('PostCancellationRequest', { guid: cancellationRequestId }, null);
    return r;
  }

  async postPendingSupplierSubmissions() {
    const r = await this.postData('PostPendingSupplierSubmissions', null, null);
    return r;
  }

  async postPendingNotificationSubmissions() {
    const r = await this.postData('PostPendingNotificationSubmissions', null, null);
    return r;
  }

  // async postResetProximityUserLogin(proximityUserId: string, role: Roles) {
  //   return this.postData<string>('postResetProximityUserLogin', null, { proximityUserId, role });
  // }

  async postSendUserInvite(proximityUserId: string): Promise<SendInviteStatus | null> {
    try {
      // Server returns SendInviteResult { Status: "Sent" | "AlreadyConfirmed" | ... }.
      // Tolerate either casing on the wire — ASP.NET Core defaults vary by controller
      // and older endpoints in this codebase serialize PascalCase.
      const result = (await this.postToUrl<Record<string, unknown>>('Login/SendConfirmationLink', null, {
        userId: proximityUserId,
      })) ?? {};
      const raw = (result['status'] ?? result['Status']) as SendInviteStatus | undefined;
      return raw ?? null;
    } catch (err: any) {
      const error = {
        name: err.name ?? 'Email invite error',
        message: err.error.Message ?? err.message,
      };

      await this.dialogService.errorDialog(error, 'Unable to send invite');
      return null;
    }
  }

  /** Bulk-invite migration tool: how many users would be invited if we sent now for this program. */
  async postPreviewInviteCountForProgram(programId: string): Promise<number> {
    return await this.postData<number>('PreviewInviteCountForProgram', { guid: programId }, null);
  }

  /** Bulk-invite migration tool: enqueue the Hangfire job to send invites for this program. */
  async postSendInvitesForProgram(programId: string): Promise<void> {
    await this.postData<string>('SendInvitesForProgram', null, { programId });
  }

  /**
   * Account User Import, identity pass. Returns one plan row per uploaded row saying whether
   * it is an Add, a Match, or a Reject — plus, for a Match, the existing ApiLink, Hire Date
   * and group memberships the client needs to work out which import rules fire.
   * One call for the whole batch.
   */
  async postPlanAccountUserImport(accountId: string, rows: ImportUserPlanRequestRow[]) {
    return await this.postData<ImportUserPlanRow[]>(
      'PlanAccountUserImport',
      { AccountId: accountId, Rows: rows },
      null,
    );
  }

  async createNotifications(notificationRequest: NotificationRequest): Promise<number> {
    const count = await this.postData<number>('CreateNotifications', notificationRequest, null);
    this.messageBusService.notify({ id: MessageTypeEnum.Notification });
    return count ?? 0;
  }

  async updateUserAllowanceLogsForUser(accountUserId: string) {
    const r = await this.postData<string>('UpdateUserAllowanceLogsForUser', { guid: accountUserId }, null);
    return r;
  }

  async updateAllUserAllowanceLogs() {
    return await this.postData<number>('UpdateAllUserAllowanceLogs', null, null);
  }

  // Consolidate N synonym tags into one canonical tag. Server enforces Rule A (no PUG
  // straddling) and Rule B (uniform scope) — see CheckTagConsolidationLegality. After this
  // returns, the synonym tag rows are deleted and all references (product/category maps,
  // issuances, and transitively all issuance user logs) point at the canonical tag.
  async consolidateProductTypeTags(canonicalTagId: string, synonymTagIds: string[]): Promise<TagConsolidationResultDto> {
    const request: TagConsolidationRequestDto = { CanonicalTagId: canonicalTagId, SynonymTagIds: synonymTagIds };
    return await this.postData<TagConsolidationResultDto>('ConsolidateProductTypeTags', request, null);
  }
}
